import React from 'react';
import {
  AudioOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import { Modal } from 'antd';
import { AutoFixHigh } from '@mui/icons-material';
import styles from './SpellPracticeModal.module.css';

export type SpellPracticeRecognitionStatus =
  | 'idle'
  | 'listening'
  | 'success'
  | 'mismatch'
  | 'error'
  | 'unsupported';

export interface SpellPracticeModalProps {
  open: boolean;
  onCancel: () => void;
  word: string;
  spanishTranslation: string;
  recognitionStatus: SpellPracticeRecognitionStatus;
  transcript: string;
  onReplay: () => void;
  onMicClick: () => void;
}

const SpellPracticeModal: React.FC<SpellPracticeModalProps> = ({
  open,
  onCancel,
  word,
  spanishTranslation,
  recognitionStatus,
  transcript,
  onReplay,
  onMicClick,
}) => {
  return (
    <Modal
      title={null}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      centered
      width={480}
      className={styles.spellModal}
    >
      <div className={styles.spellShell}>
        <div className={styles.spellHero}>
          <div className={styles.spellHeroTitles}>
            <div className={styles.spellHeroEyebrow}>How to spell</div>
            {word ? <div className={styles.spellHeroWord}>{word}</div> : null}
          </div>
        </div>
        <div className={styles.spellSheet}>
          <div className={styles.spellSectionLabel}>
            <span className={styles.spellSectionLine} aria-hidden />
            <span className={styles.spellSectionText}>Spell it out</span>
            <span className={styles.spellSectionLine} aria-hidden />
          </div>
          <div className={styles.spellLettersWrap}>
            {spanishTranslation.split('').map((char, index) => {
              const isSpace = char === ' ';
              const letterClass = [
                styles.spellLetter,
                isSpace ? styles.spellSpace : '',
                recognitionStatus === 'success' && !isSpace ? styles.spellLetterSuccess : '',
                recognitionStatus === 'mismatch' && !isSpace ? styles.spellLetterMismatch : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <span key={`${char}-${index}`} className={letterClass}>
                  {isSpace ? '\u00A0' : char}
                </span>
              );
            })}
          </div>
          <div className={styles.spellWaveTrack} aria-hidden>
            <span className={styles.spellWaveDots} />
            <span className={styles.spellWaveIconWrap}>
              <SoundOutlined className={styles.spellWaveIcon} />
            </span>
            <span className={styles.spellWaveDots} />
          </div>
          <div className={styles.spellModalActions}>
            <button
              type="button"
              className={styles.spellBtnReplay}
              onClick={onReplay}
              disabled={recognitionStatus === 'listening'}
            >
              <ReloadOutlined className={styles.spellBtnReplayIcon} />
              Play Again
            </button>
            <button
              type="button"
              className={[
                styles.spellBtnListen,
                recognitionStatus === 'listening' ? styles.spellBtnListenActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={onMicClick}
              disabled={recognitionStatus === 'unsupported'}
            >
              {recognitionStatus === 'listening' ? (
                <>
                  <span className={styles.spellListenWave} />
                  <AudioOutlined className={styles.spellBtnMicIcon} />
                  <span className={`${styles.spellListenWave} ${styles.spellListenWaveFlip}`} />
                </>
              ) : (
                <AudioOutlined className={styles.spellBtnMicIcon} />
              )}
              {recognitionStatus === 'listening' ? 'Listening…' : 'Say It'}
            </button>
          </div>
          {recognitionStatus !== 'idle' && recognitionStatus !== 'listening' ? (
            <div
              className={[
                styles.spellFeedback,
                recognitionStatus === 'success' ? styles.spellFeedbackSuccess : '',
                recognitionStatus === 'mismatch' ? styles.spellFeedbackMismatch : '',
                recognitionStatus === 'error' || recognitionStatus === 'unsupported'
                  ? styles.spellFeedbackError
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {recognitionStatus === 'success' ? (
                <>
                  <CheckCircleFilled className={styles.spellFeedbackIcon} />
                  <span>Great! You said it correctly.</span>
                </>
              ) : null}
              {recognitionStatus === 'mismatch' ? (
                <>
                  <CloseCircleFilled className={styles.spellFeedbackIcon} />
                  <span>
                    You said <strong>“{transcript || '—'}”</strong>. Try again!
                  </span>
                </>
              ) : null}
              {recognitionStatus === 'error' ? (
                <>
                  <CloseCircleFilled className={styles.spellFeedbackIcon} />
                  <span>We couldn’t hear you. Please try again.</span>
                </>
              ) : null}
              {recognitionStatus === 'unsupported' ? (
                <>
                  <CloseCircleFilled className={styles.spellFeedbackIcon} />
                  <span>Speech recognition is not supported in this browser.</span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default SpellPracticeModal;
